/**
 * Alarms store
 * @module store/alarms
 */

import { createStore, createHook } from '@thermon-platforms/react-sweet-state';
import api from 'util/api';


// define store initial state
const initialState = {
  alarms: [],
  loading: false,
  error: undefined,
  updated: undefined
};
// define the actions that mutate the state
export const actions = {
  query: (force = false) => async ({ getState, setState }) => {
    const now = Date.now();
    const currentState = getState();
    // If we're already loading alarms or the current data is still fresh, return
    if (!force && (currentState.loading || (now - currentState.updated) < (30 * 1000))) return;
    setState({ loading: true });
    try {
      // Fetch our data
      const res = await api(`/alarms`);
      // and unwrap it
      const alarms = await res.json();
      // Sort newest -> oldest by lastUpdatedAt
      const sorted = (alarms || []).sort(
        (a, b) => new Date(b?.lastUpdatedAt || 0) - new Date(a?.lastUpdatedAt || 0)
      );

      setState({ alarms: sorted, loading: false, updated: Date.now(), error: undefined });
    } catch (error) {
      console.error(error);
      // If that fails, update our state with the error
      setState({ error, loading: false, updated: Date.now() });
    }
  },
  acknowledge: (alarm) => async ({ getState, setState }) => {
    const res = await api(`/alarms/${alarm.alarmId}/acknowledge`, { method: 'PUT' });
    if (!res.ok) {
      throw new Error(`Failed to acknowledge alarm ${alarm.alarmId}`);
    }
    const updatedAlarmJson = await api(`/alarms/${alarm.alarmId}`).then(res => res.json());
    const updatedAlarm = Array.isArray(updatedAlarmJson) ? updatedAlarmJson[0] : updatedAlarmJson;
    if (!updatedAlarm) {
      throw new Error(`Failed to fetch updated alarm ${alarm.alarmId} after acknowledge`);
    }
    const next = getState().alarms.map(a =>
      a.alarmId === alarm.alarmId ? updatedAlarm : a
    );
    setState({ alarms: next });
    return updatedAlarm;
  },
  clear: (alarm) => async ({ getState, setState }) => {
    const res = await api(`/alarms/${alarm.alarmId}/clear`, { method: 'PUT' });
    if (!res.ok) {
      throw new Error(`Failed to clear alarm ${alarm.alarmId}`);
    }
    return res.json();
  }
}

// create a store type
const Store = createStore({ initialState, actions });

// create components to access store state instances
const useStore = createHook(Store);

/**
 * @function getAlarmSeverity
 * @param {object} alarm alarm model
 * @returns string
 */
export function getAlarmSeverity (alarm) {
  /*
    NOTE - this will likely change in the future but alarm types
    currently look like: HighTemperature. So, this function santizes
    the _severity_ into a string 'high'.
  */
  const alarmType = alarm?.alarmType?.toLowerCase();
  if (!alarmType) return 'ok';
  if (alarmType.includes('trip')) return 'trip';
  if (alarmType.includes('high')) return 'high';
  if (alarmType.includes('low')) return 'low';
  if (alarmType.includes('fault') || alarmType.includes('failure')) return 'fault';
  return 'info';
}

const severity = ['info', 'low', 'high', 'trip', 'fault'];
/**
 * @function getMaxSeverity
 * @param {array} alarms array of alarm models
 * @returns string
 */
export function getMaxSeverity (alarms = []) {
  if (!alarms.length) return 'ok';
  if (alarms.length == 1) return getAlarmSeverity(alarms[0]);
  // If we have multiple alarms, let's map them into indexed severity, do a sort and take the last
  // (highest) value
  const maxSeverity = alarms.map(alarm => severity.indexOf(getAlarmSeverity(alarm))).sort().pop();
  // and grab the corresponding severity string
  return severity[maxSeverity];
}

/**
 * Expose hooks for store
 * @function useStore
 * @memberof module:store/alarms
 * @example
 * const [alarmsStore, alarmsStoreActions] = useAlarmsStore();
 */
export default useStore;
